// Downloadable templates for every importer.
//
// The column order of each importer is stated in one line of prose inside its
// dialog and nowhere else, so the first thing anyone does is guess. These are
// the answer — and they live together, in one file, so a template and the parser
// it describes cannot quietly drift apart.
//
// The sample rows ARE the documentation. Each one demonstrates the thing that
// would otherwise be got wrong, which is why they are not filler:
//   * a row that leaves the optional columns blank, so "can I skip this?" is
//     answered by example rather than by trying and failing;
//   * dates written with a named month, because a numeric month-first date is
//     refused as ambiguous;
//   * an NPWP with its dots and dash, so nobody strips them thinking they are
//     decoration.
//
// Every sample must survive its own parser. A template the app rejects is worse
// than no template.

import { buildCsvForExcel, downloadCsv } from '@/lib/csv';
import { loadXlsx } from '@/lib/spreadsheet';
import { loadChunk } from '@/lib/lazyChunk';
import { downloadBlob } from '@/lib/api/invoice';
import { PRODUCT_COLUMNS } from '@/lib/api/productsExport';
import type { EventStockAllocation, Product } from '@/types/domain';

export type TemplateFormat = 'xlsx' | 'csv';

export interface ImportTemplate {
  /** Base filename, `.csv`. The xlsx writer swaps the extension. */
  filename: string;
  headers: string[];
  /** Keyed by header, matching buildCsv's row shape. */
  samples: Record<string, string>[];
}

export const STOCK_TEMPLATE: ImportTemplate = {
  filename: 'template-import-stok.csv',
  headers: ['SKU', 'Qty', 'Lot number', 'Expired date', 'Satuan', 'Form order'],
  samples: [
    {
      SKU: 'PRD-001',
      Qty: '50',
      'Lot number': 'LOT-2026-04',
      // Named month: the one shape nobody can misread. 12/31/2027 is refused,
      // because 03/04/2027 would be March or April depending on who typed it
      // and a guessed expiry reaches a customer.
      'Expired date': '31-Dec-2027',
      Satuan: 'box',
      'Form order': 'FO-2026-0012',
    },
    {
      // Lot and expiry left blank on purpose: plenty of stock has neither, and
      // this row is how the operator learns that is allowed.
      SKU: 'PRD-002',
      Qty: '10',
      'Lot number': '',
      'Expired date': '',
      Satuan: 'pcs',
      'Form order': '',
    },
  ],
};

export const CUSTOMER_TEMPLATE: ImportTemplate = {
  filename: 'template-import-customer.csv',
  headers: ['Nama', 'NIK / NPWP', 'Nomor Telepon', 'Alamat'],
  samples: [
    {
      Nama: 'Budi Santoso',
      'NIK / NPWP': '3201234567890001',        // NIK: 16 digits
      'Nomor Telepon': '081234567890',
      Alamat: 'Jl. Merdeka No. 5, Jakarta',
    },
    {
      Nama: 'Klinik Mata Sehat',
      'NIK / NPWP': '09.254.294.3-407.000',    // NPWP: punctuation is significant
      'Nomor Telepon': '02155667788',
      Alamat: 'Jl. Sudirman Kav. 21, Jakarta Selatan',
    },
  ],
};

export const PRODUCT_TEMPLATE: ImportTemplate = {
  filename: 'template-import-produk.csv',
  // Taken from the export, not retyped, so the template and a re-imported
  // export are the same document.
  headers: [...PRODUCT_COLUMNS],
  samples: [
    {
      sku: 'PRD-001',
      name: 'Ophthalmic Kaca',
      category: 'Lensa',          // by NAME; an unknown name imports with no category
      brand: 'Contoh Brand',
      price: '20000',             // plain number — "Rp 20.000" will not import
      costPrice: '12000',
      unit: 'box',
      isActive: 'true',
      description: 'Contoh deskripsi',
      imageUrl: '',
    },
    {
      sku: 'PRD-002',
      name: 'Ophthalmic Knife',
      category: '',
      price: '10000',
      costPrice: '',              // blank is allowed
      unit: 'pcs',
      isActive: 'false',
      description: '',
      imageUrl: '',
    },
  ],
};

/**
 * Hand out the template.
 *
 * xlsx by default. A CSV template put the operator into a delimiter argument
 * with Excel before they had typed a single row — which is the whole reason
 * the import felt hard. An .xlsx opens in columns, everywhere, with nothing to
 * configure. CSV stays available for anyone scripting against it.
 */
export async function downloadTemplate(
  t: ImportTemplate,
  format: TemplateFormat = 'xlsx',
): Promise<void> {
  if (format === 'csv') {
    downloadCsv(t.filename, buildCsvForExcel(t.headers, t.samples));
    return;
  }

  const XLSX = await loadXlsx();
  const aoa = [t.headers, ...t.samples.map((r) => t.headers.map((h) => r[h] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Every column wide enough to read its own sample, so nothing shows as ####.
  ws['!cols'] = t.headers.map((_, i) => ({
    wch: Math.min(Math.max(...aoa.map((row) => String(row[i] ?? '').length), 10) + 2, 46),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Template');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  downloadBlob(
    new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    t.filename.replace(/\.csv$/i, '.xlsx'),
  );
}

// ── Stock template with the catalogue inside it ─────────────────────────────
//
// The plain template left the operator to know every SKU by heart. This one
// carries the catalogue with it:
//
//   Template       — the six columns the importer reads, a SKU dropdown on
//                    column A, and column G filling in the product name for
//                    whatever SKU was chosen, so a wrong one shows at once.
//   Daftar produk  — every active product: SKU, name, price, unit.
//   Petunjuk       — the rules and two worked examples, kept off the Template
//                    sheet so no example row is ever imported by accident.
//
// The importer only reads the first sheet and columns A–F, so the extra sheets
// and column G cost nothing on the way back in.
//
// SheetJS community edition cannot write data validation, so the dropdown is
// added to the sheet XML after SheetJS has built the file. In Microsoft 365 and
// Google Sheets the dropdown is searchable by typing; older Excel shows the
// same list without search.

const TEMPLATE_ROWS = 500;

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 31-Dec-2027 — the date shape the importer can never misread. UTC, like the stored date. */
const namedMonthDate = (ms: number) => {
  const d = new Date(ms);
  return `${String(d.getUTCDate()).padStart(2, '0')}-${MONTH_ABBR[d.getUTCMonth()]}-${d.getUTCFullYear()}`;
};
const PRODUCT_SHEET = 'Daftar produk';

/** Elements that must come after <dataValidations> in a worksheet (ECMA-376 order). */
const AFTER_DATA_VALIDATIONS = [
  'hyperlinks', 'printOptions', 'pageMargins', 'pageSetup', 'headerFooter', 'rowBreaks',
  'colBreaks', 'customProperties', 'cellWatches', 'ignoredErrors', 'smartTags', 'drawing',
  'legacyDrawing', 'legacyDrawingHF', 'picture', 'oleObjects', 'controls', 'webPublishItems',
  'tableParts', 'extLst',
];

const xmlEscape = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function injectSkuDropdown(sheetXml: string, productCount: number, rows: number): string {
  const last = Math.max(2, productCount + 1);
  const validation =
    `<dataValidations count="1">` +
    `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1" errorStyle="stop" ` +
    `promptTitle="SKU" prompt="${xmlEscape('Pilih dari daftar, atau ketik SKU untuk mencari.')}" ` +
    `errorTitle="SKU tidak dikenal" error="${xmlEscape(`SKU ini tidak ada di sheet "${PRODUCT_SHEET}".`)}" ` +
    `sqref="A2:A${rows + 1}">` +
    `<formula1>'${PRODUCT_SHEET}'!$A$2:$A$${last}</formula1>` +
    `</dataValidation></dataValidations>`;

  let at = -1;
  for (const tag of AFTER_DATA_VALIDATIONS) {
    const i = sheetXml.indexOf(`<${tag}`);
    if (i !== -1 && (at === -1 || i < at)) at = i;
  }
  if (at === -1) at = sheetXml.lastIndexOf('</worksheet>');
  if (at === -1) throw new Error('Template Excel tidak bisa dibuat (struktur sheet tidak dikenal).');
  return sheetXml.slice(0, at) + validation + sheetXml.slice(at);
}

export interface StockTemplateOptions {
  /**
   * The event's current lots. When given, the import sheet starts with one
   * row per lot as it stands now — change a qty, add rows for new lots, import
   * back. Qty in the sheet is the lot's TOTAL brought, not an addition, which
   * is also what the importer writes; untouched rows come back unchanged.
   */
  allocations?: ReadonlyArray<EventStockAllocation>;
  /** Goes into the filename so two events' sheets are not confused. */
  eventCode?: string;
}

export interface StockWorkbookOptions extends StockTemplateOptions {
  /**
   * The stock report of "Export stok", placed right after the import sheet.
   * The import sheet is then called "Untuk import" and opens first — the owner
   * opened the export, saw only the report and concluded the template was not
   * in it. The importer finds that sheet by name either way.
   */
  report?: { name: string; aoa: (string | number)[][]; cols?: { wch: number }[] };
}

/**
 * Sheet names the importer reads before falling back to the first sheet.
 * Kept in step with spreadsheetFileToText() (which cannot import this module
 * without a cycle).
 */
export const IMPORT_SHEET_NAMES = ['Template', 'Untuk import'] as const;

export async function buildStockWorkbook(
  products: ReadonlyArray<Product>,
  options: StockWorkbookOptions = {},
): Promise<Uint8Array<ArrayBuffer>> {
  const [XLSX, fflate] = await Promise.all([
    loadXlsx(),
    loadChunk(() => import('fflate'), 'Excel'),
  ]);

  const list = [...products]
    .filter((p) => p.isActive && p.sku.trim())
    .sort((a, b) => a.name.localeCompare(b.name));
  const importName = options.report ? IMPORT_SHEET_NAMES[1] : IMPORT_SHEET_NAMES[0];

  // ── Import sheet ──
  // Current stock first, grouped by product and earliest expiry — the order
  // the stock page shows it in. SKU from the catalogue, which is what the
  // importer matches on, falling back to the snapshot on the lot.
  const skuById = new Map(products.map((p) => [p.id, p.sku]));
  const current = [...(options.allocations ?? [])]
    .filter((a) => !a.closedAt)
    .sort((a, b) =>
      a.productName.localeCompare(b.productName) ||
      (a.expiryDate ?? Infinity) - (b.expiryDate ?? Infinity));
  const prefilled = current.map((a) => [
    skuById.get(a.productId) ?? a.productSku,
    a.qtyIn,
    a.lotNumber || '',
    a.expiryDate ? namedMonthDate(a.expiryDate) : '',
    a.unit || '',
    a.orderForm || '',
  ]);
  const rows = Math.max(TEMPLATE_ROWS, prefilled.length + 200);

  const headers = [...STOCK_TEMPLATE.headers, 'Nama produk (otomatis)'];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...prefilled]);
  for (let r = 2; r <= rows + 1; r++) {
    ws[`G${r}`] = {
      t: 's',
      v: '',
      f: `IF($A${r}="","",IFERROR(VLOOKUP($A${r},'${PRODUCT_SHEET}'!$A:$B,2,FALSE),"SKU tidak ada di daftar"))`,
    };
  }
  ws['!ref'] = `A1:G${rows + 1}`;
  ws['!cols'] = [
    { wch: 26 }, { wch: 8 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 52 },
  ];

  // ── Daftar produk ──
  const productRows = [
    ['SKU', 'Nama produk', 'Harga', 'Satuan'],
    ...list.map((p) => [p.sku, p.name, p.price, p.unit ?? '']),
  ];
  const wsProducts = XLSX.utils.aoa_to_sheet(productRows);
  wsProducts['!cols'] = [
    { wch: Math.min(Math.max(10, ...list.map((p) => p.sku.length)) + 2, 40) },
    { wch: Math.min(Math.max(20, ...list.map((p) => p.name.length)) + 2, 80) },
    { wch: 12 },
    { wch: 8 },
  ];

  // ── Petunjuk ──
  const example = STOCK_TEMPLATE.samples;
  const wsHelp = XLSX.utils.aoa_to_sheet([
    ['Cara mengisi stok event untuk di-import'],
    [],
    ...(options.report
      ? [['0', `Sheet "${options.report.name}" adalah laporan stok (hanya dibaca). Untuk mengubah stok, pakai sheet "${importName}".`]]
      : []),
    ['1', `Isi di sheet "${importName}". Sheet lain tidak dibaca saat import.`],
    ['', current.length > 0
      ? `Baris paling atas sudah berisi ${current.length} lot yang ada di event sekarang. Ubah Qty-nya, atau tambah baris di bawahnya untuk lot baru. Baris yang tidak diubah dilewati saat import.`
      : 'Event ini belum punya stok — mulai isi dari baris 2.'],
    ['', 'Qty = jumlah TOTAL yang dibawa untuk lot itu, bukan tambahan. Contoh: tertulis 20, mau jadi 30 → ketik 30.'],
    ['2', 'Kolom SKU: klik sel lalu pilih dari daftar (atau ketik untuk mencari). Kolom G menampilkan nama produknya — kalau muncul "SKU tidak ada di daftar", SKU-nya salah.'],
    ['3', 'Qty wajib, angka bulat.'],
    ['4', 'Lot number, Expired date, Satuan, Form order boleh kosong.'],
    ['5', 'Tanggal paling aman ditulis dengan nama bulan: 31-Dec-2027. Format 12/31/2027 ditolak karena bisa terbaca terbalik.'],
    ['6', 'Satu produk boleh beberapa baris untuk beberapa lot. SKU + lot + tanggal yang sama akan memperbarui qty, bukan menggandakan.'],
    [],
    [`Contoh (jangan disalin ke ${importName} dengan SKU ini — hanya contoh bentuk):`],
    STOCK_TEMPLATE.headers,
    ...example.map((r) => STOCK_TEMPLATE.headers.map((h) => r[h] ?? '')),
  ]);
  wsHelp['!cols'] = [{ wch: 14 }, { wch: 110 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 16 }];

  const wb = XLSX.utils.book_new();
  // The editable sheet first: it is what the file is opened for.
  XLSX.utils.book_append_sheet(wb, ws, importName);
  if (options.report) {
    const wsReport = XLSX.utils.aoa_to_sheet(options.report.aoa);
    if (options.report.cols) wsReport['!cols'] = options.report.cols;
    wsReport['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, wsReport, options.report.name);
  }
  XLSX.utils.book_append_sheet(wb, wsProducts, PRODUCT_SHEET);
  XLSX.utils.book_append_sheet(wb, wsHelp, 'Petunjuk');
  const bytes = new Uint8Array(XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer);

  // ── Add the SKU dropdown to the import sheet ──
  // SheetJS names sheet parts sheet1.xml, sheet2.xml … in workbook order.
  const files = fflate.unzipSync(bytes);
  const sheetPath = `xl/worksheets/sheet${wb.SheetNames.indexOf(importName) + 1}.xml`;
  if (!files[sheetPath]) throw new Error('File Excel tidak bisa dibuat (sheet import tidak ditemukan).');
  const xml = fflate.strFromU8(files[sheetPath]);
  files[sheetPath] = fflate.strToU8(injectSkuDropdown(xml, list.length, rows));
  // Copied into a plain ArrayBuffer-backed array, which is what Blob accepts.
  return new Uint8Array(fflate.zipSync(files, { level: 6 }));
}

export async function downloadStockTemplate(
  products: ReadonlyArray<Product>,
  options: StockTemplateOptions = {},
): Promise<void> {
  const out = await buildStockWorkbook(products, options);
  downloadBlob(
    new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    options.eventCode ? `stok-${options.eventCode}-untuk-import.xlsx` : 'template-import-stok.xlsx',
  );
}
